import { HISTORY_STORAGE_KEY } from 'constants/ui/storageKey';
import { useCallback } from 'react';
import { useNavigate, useLocation, useParams, useSearchParams, NavigateOptions } from 'react-router-dom';

type RouteParams = Record<string, string>;

type QueryParams = Record<string, string | number | boolean | undefined | Array<string | number>>;

interface NavigationHistoryEntry {
  path: string;
  timestamp: number;
}

// Navigation options extending React Router's NavigateOptions
interface AppNavigateOptions extends NavigateOptions {
  replace?: boolean;
  preserveQuery?: boolean;
  openNewTab?: boolean;
}

export const useAppNavigation = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams();
  const [searchParams, setSearchParams] = useSearchParams();

  // Get the navigation history from sessionStorage
  const getNavigationHistory = useCallback((): NavigationHistoryEntry[] => {
    try {
      const historyString = sessionStorage.getItem(HISTORY_STORAGE_KEY);
      return historyString ? JSON.parse(historyString) : [];
    } catch (error) {
      console.error('Failed to retrieve navigation history:', error);
      return [];
    }
  }, []);

  // Get query parameters as an object
  const getQueryParams = useCallback((): Record<string, string> => {
    const params: Record<string, string> = {};
    searchParams.forEach((value, key) => {
      params[key] = value;
    });
    return params;
  }, [searchParams]);

  // Format the query parameter value
  const formatQueryParamValue = useCallback((value: string | number | boolean | Array<string | number>) => {
    if (Array.isArray(value)) {
      return value.join(',');
    }
    return String(value);
  }, []);

  // Navigate to a specific route
  const navigateTo = useCallback(
    (path: string, routeParams?: RouteParams, queryParams?: QueryParams, options?: AppNavigateOptions) => {
      // Apply route parameters to path
      let processedPath = path;
      if (routeParams) {
        Object.entries(routeParams).forEach(([key, value]) => {
          processedPath = processedPath.replace(`:${key}`, encodeURIComponent(value));
        });
      }

      // Process query parameters
      let finalPath = processedPath;
      if (queryParams) {
        const currentQuery = options?.preserveQuery ? getQueryParams() : {};
        const mergedQuery = { ...currentQuery, ...queryParams };
        // Remove undefined values
        Object.keys(mergedQuery).forEach(key => {
          if (mergedQuery[key] === undefined) {
            delete mergedQuery[key];
          }
        });
        // Build query string if there are params
        if (Object.keys(mergedQuery).length > 0) {
          const queryString = new URLSearchParams(
            Object.entries(mergedQuery).map(([k, v]) => {
              const formattedValue = Array.isArray(v) ? formatQueryParamValue(v) : String(v);
              return [k, formattedValue];
            })
          ).toString();

          finalPath = `${processedPath}?${queryString}`;
        }
      }

      if (options?.openNewTab) {
        return window.open(finalPath, '_blank');
      }

      if (options?.replace) {
        navigate(finalPath, { replace: true, state: options.state });
      } else {
        navigate(finalPath, { state: options?.state });
      }
    },
    [navigate, location.pathname, getQueryParams]
  );

  const goBack = useCallback(() => {
    navigate(-1);
  }, [navigate]);

  // Navigate to home
  const goHome = useCallback(() => {
    navigateTo('/', undefined, undefined, { replace: true });
  }, [navigateTo]);

  // Update query parameters without navigation
  const updateQueryParams = useCallback(
    (params: QueryParams) => {
      const currentParams = getQueryParams();
      const newParams: Record<string, string> = { ...currentParams };

      // Update or remove parameters
      Object.entries(params).forEach(([key, value]) => {
        // Remove parameter if undefined or empty array
        if (value === undefined || (Array.isArray(value) && value.length === 0) || value === '') {
          delete newParams[key];
        }
        // Handle arrays
        else if (Array.isArray(value)) {
          newParams[key] = value.join(','); // Use raw comma that URLSearchParams will encode properly
        }
        // Set simple values
        else {
          newParams[key] = String(value);
        }
      });

      // Create URLSearchParams from the cleaned object
      const searchParams = new URLSearchParams();
      for (const [key, value] of Object.entries(newParams)) {
        searchParams.set(key, value);
      }

      setSearchParams(searchParams);
    },
    [getQueryParams, setSearchParams]
  );

  // Clear all query parameters
  const clearQueryParams = useCallback(() => {
    setSearchParams(new URLSearchParams());
  }, [setSearchParams]);

  const getArrayParamsValue = useCallback(
    (key: string): string[] => {
      const value = searchParams.get(key);
      if (!value) return [];
      return value
        .split(',')
        .filter(Boolean)
        .map(item => item.trim());
    },
    [searchParams]
  );

  const validateQueryParams = useCallback(
    (paramsToValidate: Record<string, Array<string | number>>) => {
      const currentParams = getQueryParams();
      const newParams: Record<string, string> = { ...currentParams };

      Object.entries(paramsToValidate).forEach(([key, validList]) => {
        if (!newParams[key]) return; // If param doesn't exist, skip

        // Convert value to array for comparison
        const currentValues = decodeURIComponent(newParams[key])
          .split(',')
          .map(v => v.trim())
          .filter(Boolean);

        // Keep only those that are in the valid list
        const filteredValues = currentValues.filter(v => validList.map(String).includes(v));

        if (filteredValues.length > 0) {
          newParams[key] = filteredValues.join(',');
        } else {
          delete newParams[key]; // Remove param if nothing valid
        }
      });

      // Apply cleaned query params back to URL
      const searchParams = new URLSearchParams();
      for (const [key, value] of Object.entries(newParams)) {
        searchParams.set(key, value);
      }
      setSearchParams(searchParams);
    },
    [getQueryParams, setSearchParams]
  );

  return {
    navigateTo,
    goHome,
    goBack,
    currentPath: location.pathname,
    currentQuery: getQueryParams(),
    params,
    updateQueryParams,
    clearQueryParams,
    isFirstVisit: getNavigationHistory().length <= 1,
    getArrayParamsValue,
    validateQueryParams,
  };
};

export default useAppNavigation;

/**
 * Usages in components:
 *
 * Features:
 * - navigateTo('/users/:userId', { userId: '123' }) - Navigate to /users/123
 * - navigateTo('/search', undefined, { query: 'react', sort: 'name' }); -  Navigate to /search?query=react&sort=name
 * - navigateTo('/search', undefined, { page: 2 }, { preserveQuery: true }); - Only update 'page' parameter, keep others
 * - goBack(); // Go back to previous page in app history
 * - updateQueryParams({ status: 'active', sort: 'date' } Update filters without page reload/navigation
  const handleJiraNavigate = () => {
    navigateTo('https://esofttech.atlassian.net/jira/software/projects/EP/boards/110', undefined, {
      issueParent: [75676, 75677],
      issueType: [10254, 10250, 10252]
    });
  updateQueryParams({
      // This will be formatted as "75676%2C75677" in the URL
      issueParent: [75676, 75677],
      
      // This will be formatted as "10254%2C10250%2C10252" in the URL
      issueType: [10254, 10250, 10252]
    });
  };
 */
